import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from "react";
import ApiFactory from "neon-frontend-utils/src/api/factory";
import Logger from "neon-frontend-utils/src/logger";
import Components from "../../constants/Components";
import DashboardTypes from "../../constants/DashboardType";

interface FeatureFlagsState {
    features: Record<string, boolean>;
    canShowDashboards: boolean;
    availableDashboardTypes: DashboardTypes[];
    hasTalentAnalytics: boolean;
    hasLiveAssessmentAccess: boolean;
    checkingAccess: boolean;
    isTalentAnalyticsReceiver: boolean;
    isAssessmentOutcomeEnabled: boolean;
    isFeatureFlagsAccessLoaded: boolean;
}

interface FeatureFlagsAccessProviderProps {
    children: ReactNode;
}

const allDashboards = [
    DashboardTypes.BASIC,
    DashboardTypes.RANKING,
    DashboardTypes.TEAM,
    DashboardTypes.BENCHMARK,
    DashboardTypes.TEAM_SESSION,
    DashboardTypes.LASSO,
    DashboardTypes.BOARD_SCAN,
    DashboardTypes.REFLECTION_TEXT,
];

const defaultFeatureFlagsState: FeatureFlagsState = {
    availableDashboardTypes: [],
    canShowDashboards: false,
    checkingAccess: false,
    features: {
        teams: false,
    },
    hasLiveAssessmentAccess: false,
    hasTalentAnalytics: false,
    isAssessmentOutcomeEnabled: false,
    isFeatureFlagsAccessLoaded: false,
    isTalentAnalyticsReceiver: false,
};

export const FeatureFlagsAccessContext = createContext<FeatureFlagsState>(defaultFeatureFlagsState);

export const FeatureFlagsAccessProvider = ({ children }: FeatureFlagsAccessProviderProps) => {
    const apiPlatform = ApiFactory.get("neon-v2");
    const api = ApiFactory.get("neon");
    const user = api.getAuthenticator().getUser();

    const [features, setFeatures] = useState<Record<string, boolean>>({});

    const [canShowDashboards, setCanShowDashboards] = useState(false);
    const [availableDashboardTypes, setAvailableDashboardTypes] = useState<DashboardTypes[]>([]);
    const [hasTalentAnalytics, setHasTalentAnalytics] = useState(false);
    const [hasLiveAssessmentAccess, setHasLiveAssessmentAccess] = useState(false);
    const [isAssessmentOutcomeEnabled, setIsAssessmentOutcomeEnabled] = useState(false);
    const [isFeatureFlagsAccessLoaded, setIsFeatureFlagsAccessLoaded] = useState(false);

    // Some users have no access to Talent Analytics, but they can receive Talent Analytics reports
    const [isTalentAnalyticsReceiver, setIsTalentAnalyticsReceiver] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);

    const getFeatureFlags = () => {
        setIsFeatureFlagsAccessLoaded(false);
        apiPlatform
            .get(apiPlatform.getBaseUrl(), apiPlatform.getEndpoints().participants.getFeatureFlags, {})
            .then((response) => {
                if (response.error || (response.errors && response.errors.length)) {
                    throw new Error();
                }
                setIsFeatureFlagsAccessLoaded(true);

                // Once there are more features, we should only save features array in this context
                // and do all logic of filtering in separate files, where particular feature is needed
                const dashboardsAccess = Object.keys(response).filter(
                    (key) => allDashboards.includes(key as DashboardTypes) && response[key] === true,
                );
                const hasTalentAnalyticsAccess = response.talent_analytics === true;

                setCanShowDashboards(hasTalentAnalyticsAccess || !!dashboardsAccess.length);
                setAvailableDashboardTypes(
                    hasTalentAnalyticsAccess ? allDashboards : dashboardsAccess.map((key) => key as DashboardTypes),
                );
                setHasTalentAnalytics(hasTalentAnalyticsAccess);
                setIsTalentAnalyticsReceiver(response.talent_analytics_receiver === true);
                setCheckingAccess(false);
                setHasLiveAssessmentAccess(response.live_assessment === true);
                setIsAssessmentOutcomeEnabled(response.assessment_outcome === true);

                setFeatures(response);
            })
            .catch((error) => {
                setCheckingAccess(false);
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Unsuccessful check for feature flags",
                });
            });
    };

    useEffect(() => {
        if (!user) {
            return;
        }
        getFeatureFlags();
    }, []);

    const providerValue = useMemo(
        () => ({
            availableDashboardTypes,
            canShowDashboards,
            checkingAccess,
            features,
            hasLiveAssessmentAccess,
            hasTalentAnalytics,
            isAssessmentOutcomeEnabled,
            isFeatureFlagsAccessLoaded,
            isTalentAnalyticsReceiver,
        }),
        [
            availableDashboardTypes,
            canShowDashboards,
            checkingAccess,
            features,
            hasLiveAssessmentAccess,
            hasTalentAnalytics,
            isAssessmentOutcomeEnabled,
            isFeatureFlagsAccessLoaded,
            isTalentAnalyticsReceiver,
        ],
    );

    return <FeatureFlagsAccessContext.Provider value={providerValue}>{children}</FeatureFlagsAccessContext.Provider>;
};

export const useFeatureFlagsAccess = (): FeatureFlagsState => {
    const context = useContext(FeatureFlagsAccessContext);

    if (!context) {
        throw new Error("useFeatureFlagsAccess must be used within a FeatureFlagsAccessProvider");
    }
    return context;
};

export default FeatureFlagsAccessContext;
